import { useState, type FormEvent } from 'react'
import { MessageSquarePlus } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import {
  useAppSelector,
  useCreateBugReportMutation,
  type CreateBugReportPayload
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'

export function buildFeedbackPayload(input: {
  title: string
  description: string
  pagePath: string
  conversationId: string | null
}): CreateBugReportPayload {
  return {
    title: input.title.trim(),
    description: input.description.trim(),
    page_path: input.pagePath,
    ...(input.conversationId ? { conversation_id: input.conversationId } : {})
  }
}

function readErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.trim()) return message
    }
  }
  return 'The report could not be sent. Please try again.'
}

export function FeedbackDialog({ audience = 'engineer' }: { audience?: 'engineer' | 'operator' }) {
  const location = useLocation()
  const conversationId = useAppSelector((state) => state.agentOrchestrator.activeTabId)
  const [createFeedback, { isLoading }] = useCreateBugReportMutation()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setTitle('')
    setDescription('')
    setError(null)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen && !isLoading) reset()
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const payload = buildFeedbackPayload({
      title,
      description,
      pagePath: `${location.pathname}${location.search}${location.hash}`,
      conversationId
    })
    if (!payload.title || !payload.description) return

    setError(null)
    try {
      await createFeedback(payload).unwrap()
      reset()
      setOpen(false)
    } catch (caught) {
      setError(readErrorMessage(caught))
    }
  }

  const trigger =
    audience === 'operator' ? (
      <button type="button" className="tv-nav-btn mt-auto">
        <MessageSquarePlus className="size-5" strokeWidth={1.6} />
        <span>Feedback</span>
      </button>
    ) : (
      <Button
        type="button"
        variant="ghost"
        className="h-10 w-full justify-start gap-1.5 px-2 duration-200 hover:bg-brand-soft/50 hover:text-ink dark:hover:bg-brand/15"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md md:-ml-[2px]">
          <MessageSquarePlus className="size-4.5" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 overflow-hidden text-11 font-medium tracking-[0.08em] whitespace-nowrap uppercase">
          Feedback
        </span>
      </Button>
    )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>
              Share a bug or a feature request. The current page and active conversation will be
              included automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-1.5">
            <label htmlFor="feedback-title" className="text-sm font-medium">
              Title
            </label>
            <Input
              id="feedback-title"
              value={title}
              maxLength={200}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <label htmlFor="feedback-description" className="text-sm font-medium">
              Description
            </label>
            <Textarea
              id="feedback-description"
              value={description}
              maxLength={5000}
              rows={7}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          {error ? (
            <p role="alert" className="m-0 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="submit" disabled={isLoading || !title.trim() || !description.trim()}>
              {isLoading ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
